/**
 * Sponsored memberships (Sheepdog build, migration 2026-10-19-580). READ-ONLY views of what the CRM derives from
 * Zuora: which households an organization pays for (Zuora invoice owner + SponsorshipProgram /
 * SponsorshipAgreementId on the household's subscription). Nothing here changes memberships, journeys or tasks.
 * All three RPCs are internal-staff only (they raise 42501 for anyone else).
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface ContactSponsorship {
  sponsored: boolean;
  status?: "active" | "ended";
  program?: string | null;
  agreement_ref?: string | null;
  agreement_status?: "needs_review" | "active" | "ended" | null;
  organization_id?: UUID | null;
  organization_name?: string | null;
  since?: string | null;
  ended_on?: string | null;
}

export interface SponsorshipAgreementRow {
  agreement_id: UUID;
  agreement_ref: string;
  program: string;
  status: "needs_review" | "active" | "ended";
  link_source: "auto" | "staff";
  seat_allotment: number | null;
  seats_covered: number;
  seats_ended: number;
  starts_on: string | null;
  ends_on: string | null;
  payer_account_number: string | null;
  first_seen_at: string;
}

export interface SponsoredMemberRow {
  agreement_ref: string;
  zuora_account_id: string;
  account_number: string | null;
  contact_id: UUID | null;
  contact_name: string | null;
  covers: boolean;
  status: string | null;
  since: string | null;
  ended_on: string | null;
}

export const programDisplay = (p?: string | null): string =>
  !p ? "Sponsored" : p === "sheepdog" ? "Sheepdog" : p.replace(/_/g, " ");

export async function getContactSponsorship(
  contactId: UUID,
): Promise<ContactSponsorship> {
  const { data, error } = await supabase.rpc("sponsorship_for_contact", {
    p_contact_id: contactId,
  });
  if (error) {
    throw error;
  }
  return (data as ContactSponsorship) ?? { sponsored: false };
}

export async function getOrgSponsorshipAgreements(
  organizationId: UUID,
): Promise<SponsorshipAgreementRow[]> {
  const { data, error } = await supabase.rpc("sponsorship_org_agreements", {
    p_organization_id: organizationId,
  });
  if (error) {
    throw error;
  }
  return (data as SponsorshipAgreementRow[]) ?? [];
}

export async function getOrgSponsoredMembers(
  organizationId: UUID,
): Promise<SponsoredMemberRow[]> {
  const { data, error } = await supabase.rpc("sponsorship_org_members", {
    p_organization_id: organizationId,
  });
  if (error) {
    throw error;
  }
  return (data as SponsoredMemberRow[]) ?? [];
}

/** (4) Read-only attribution: the paying organization's Concierge rep. No commission logic. */
export interface SponsorshipCredit {
  credited_to: "concierge" | "none";
  profile_id?: UUID;
  name?: string;
  is_active?: boolean;
  reason?: string;
}

export async function getOrgSponsorshipCredit(
  organizationId: UUID,
): Promise<SponsorshipCredit> {
  const { data, error } = await supabase.rpc("sponsorship_org_credit", {
    p_organization_id: organizationId,
  });
  if (error) {
    throw error;
  }
  return data as SponsorshipCredit;
}

/** (5) Existing Zuora business accounts: comp-seat allotments entered by staff, with history (migration 581). */
export interface BusinessAllotment {
  seats: number;
  effective_from: string;
  reason: string;
  entered_at: string;
  entered_by: string | null;
}

export interface BusinessAccountSummary {
  business_accounts: {
    business_account_id: string;
    name: string | null;
    link_source: "auto" | "staff";
  }[];
  member_accounts: number;
  active_members: number;
  active_comp_type: number;
  active_zero_mrr: number;
  active_comp_either: number;
  allotment: BusinessAllotment | null;
  history: BusinessAllotment[];
}

export interface BusinessMemberRow {
  business_account_name: string | null;
  zuora_account_id: string;
  account_number: string | null;
  account_type: string | null;
  mrr: number | null;
  active: boolean;
  contact_id: UUID | null;
  contact_name: string | null;
}

export interface BusinessAccountSearchRow {
  business_account_id: string;
  name: string | null;
  member_accounts: number;
  organization_id: UUID | null;
  organization_name: string | null;
}

export async function getBusinessAccountSummary(
  organizationId: UUID,
): Promise<BusinessAccountSummary> {
  const { data, error } = await supabase.rpc("business_account_org_summary", {
    p_organization_id: organizationId,
  });
  if (error) {
    throw error;
  }
  return data as BusinessAccountSummary;
}

export async function getBusinessAccountMembers(
  organizationId: UUID,
): Promise<BusinessMemberRow[]> {
  const { data, error } = await supabase.rpc("business_account_org_members", {
    p_organization_id: organizationId,
  });
  if (error) {
    throw error;
  }
  return (data as BusinessMemberRow[]) ?? [];
}

export async function searchBusinessAccounts(
  query: string,
): Promise<BusinessAccountSearchRow[]> {
  const { data, error } = await supabase.rpc("business_account_search", {
    p_query: query,
  });
  if (error) {
    throw error;
  }
  return (data as BusinessAccountSearchRow[]) ?? [];
}

export async function linkBusinessAccount(
  businessAccountId: string,
  organizationId: UUID | null,
): Promise<void> {
  const { error } = await supabase.rpc("business_account_link", {
    p_business_account_id: businessAccountId,
    p_organization_id: organizationId,
  });
  if (error) {
    throw error;
  }
}

export async function addCompAllotment(
  organizationId: UUID,
  seats: number,
  effectiveFrom: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc("business_comp_allotment_add", {
    p_organization_id: organizationId,
    p_seats: seats,
    p_effective_from: effectiveFrom,
    p_reason: reason,
  });
  if (error) {
    throw error;
  }
}

export const friendlyRpcError = (e: Error): string => {
  const m = e.message || "";
  if (
    m.includes("ALLOTMENT_INVALID:") ||
    m.includes("BUSINESS_LINK_INVALID:")
  ) {
    return m.replace(/^.*(ALLOTMENT_INVALID|BUSINESS_LINK_INVALID):\s*/, "");
  }
  if (m.includes("not allowed")) {
    return "You do not have permission to do that.";
  }
  return m;
};
